import { Intent } from '@blueprintjs/core';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { useAppDispatch } from 'src/ui/app/hooks';
import { queueMessage } from 'src/ui/features/notifications';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { getSdk } from 'src/ui/graph/sdk';
import { Connection, TimeGrain, UnitUsageRecord } from 'src/ui/graph/types';
import * as copy from 'src/ui/templates/copy';
import { exportToFile } from 'src/ui/utils/exportToFile';
import {
  GraphError,
  getErrorFromGraphQL,
} from 'src/ui/utils/getErrorFromGraphQL';

function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return getErrorFromGraphQL(error as GraphError).message;
  }
  if (error instanceof Error) return error.message;
  return 'Unknown error occurred';
}

export const useDownloadSdu = (connection: Connection) => {
  const { org = '' } = useParams<{ org?: string }>();
  const dispatch = useAppDispatch();

  return React.useCallback(async () => {
    try {
      dispatch(
        queueMessage({
          message: copy.components.stardogUnitUsage.downloadAttempting(
            connection.name
          ),
          intent: Intent.NONE,
        })
      );

      // End date only; start date defaults to earliest DailyUsage on the server
      const endDate = new Date();
      const formatDate = (date: Date) => date.toISOString().split('T')[0];
      const endDateStr = formatDate(endDate);

      const sdk = getSdk(graphQLClient);
      const data = await sdk.getUnitUsage({
        connectionId: connection.id,
        startDate: null,
        endDate: endDateStr,
        org_domain: org || connection.organization?.domain || null,
        modules: null,
        timeGrain: TimeGrain.Day,
      });

      const usageRecords = (data?.getUnitUsage || []).filter(
        (record): record is UnitUsageRecord => record !== null
      );

      // Aggregate daily totals across all modules; keep remarks per date (same for all modules on that date)
      const dailyTotals = new Map<string, number>();
      const dateToRemarks = new Map<string, string>();
      usageRecords.forEach((record: UnitUsageRecord) => {
        if (record.date) {
          const currentTotal = dailyTotals.get(record.date) || 0;
          const sum = currentTotal + record.totalUnitsConsumed;
          dailyTotals.set(record.date, Math.round(sum * 100) / 100);
          if (record.remarks != null && record.remarks !== '') {
            dateToRemarks.set(record.date, record.remarks);
          }
        }
      });

      // Convert to sorted array of [date, total] pairs
      const sortedDailyTotals = Array.from(
        dailyTotals.entries()
      ).sort(([dateA], [dateB]) => dateA.localeCompare(dateB));

      if (sortedDailyTotals.length === 0) {
        dispatch(
          queueMessage({
            message: copy.components.stardogUnitUsage.downloadNoData(
              connection.name
            ),
            intent: Intent.WARNING,
          })
        );
        return;
      }

      // Escape CSV cell if it contains comma or newline
      const escapeCsv = (cell: string) =>
        /[,\n"]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;

      // Convert the usage records to CSV format (with Remarks column)
      const headerRow = copy.components.stardogUnitUsage.csvHeaders.join(',');
      const dataRows = sortedDailyTotals.map(([date, dailyTotal]) => {
        const remarks = dateToRemarks.get(date) ?? '';
        return `${date},${escapeCsv(connection.name)},${escapeCsv(
          connection.endpoint
        )},${dailyTotal},${escapeCsv(remarks)}`;
      });
      let grandTotal = 0;
      sortedDailyTotals.forEach(([, dailyTotal]) => {
        grandTotal += dailyTotal;
      });
      const rangeStart = sortedDailyTotals[0][0];
      const totalRow = [
        'TOTAL',
        '',
        '',
        Math.round(grandTotal * 100) / 100,
        '',
      ].join(',');
      const csvContent = [headerRow, ...dataRows, totalRow].join('\n');

      exportToFile({
        contents: csvContent,
        filename: `stardog-unit-usage-${connection.name}-${rangeStart}-${endDateStr}.csv`,
        mimeType: 'text/csv',
      });

      dispatch(
        queueMessage({
          message: `Successfully downloaded Stardog Unit Usage for "${connection.name}".`,
          intent: Intent.SUCCESS,
        })
      );
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      dispatch(
        queueMessage({
          message: `${copy.components.stardogUnitUsage.downloadSyncFailed} "${connection.name}": ${errorMessage}`,
          intent: Intent.DANGER,
        })
      );
    }
  }, [connection, dispatch, org]);
};
